import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BiPlus, BiPencil, BiTrash, BiExport, BiImport } from "react-icons/bi";
import axios from "axios";
import { API_BASE_URL } from "../utils/urls.js";
import axiosClient from "../utils/apiClient.js";

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // search & pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // formatting helper
  const formatLabel = (value) => {
    if (!value) return "";
    return value
      .toString()
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // sanitize search: trim, collapse multiple spaces to single space
  const normalizeSearch = (s) => s.trim().replace(/\s+/g, " ");

  // fetch users from server (server-side search + pagination)
  useEffect(() => {
    setSelectedIds([]);
    let cancelled = false;
    const debounce = setTimeout(async () => {
      setLoading(true);
      setError(null);

      try {
        const normalized = normalizeSearch(searchQuery);
        // encode the query component
        const q = encodeURIComponent(normalized);

        const resp = await axiosClient.get(
          `/clients?page=${page}&limit=${limit}${q ? `&search=${q}` : ""}`
        );

        if (cancelled) return;

        // backend response expected to be { page, limit, total, totalPages, data }
        const payload = resp.data || {};
        setUsers(Array.isArray(payload.data) ? payload.data : []);
        setTotal(payload.total ?? 0);
        setTotalPages(payload.totalPages ?? 1);
      } catch (err) {
        if (!cancelled) {
          setError("Failed to fetch users. Please try again.");
          console.error("Error fetching users:", err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300); // debounce 300ms

    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [page, limit, searchQuery]);

  const handleToggleSelect = (userId) => {
    setSelectedIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (users.length > 0 && selectedIds.length === users.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(users.map((u) => u.id));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (
      !window.confirm(
        `Are you sure you want to delete the ${selectedIds.length} selected client(s)?`
      )
    )
      return;

    setIsDeleting(true);
    setError(null);
    const token = localStorage.getItem("token");
    try {
      await axios.post(
        `${API_BASE_URL}/users/bulk-delete`,
        { userIds: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedIds([]);
      // Refetch
      const normalized = normalizeSearch(searchQuery);
      const q = encodeURIComponent(normalized);
      const resp = await axiosClient.get(
        `/clients?page=${page}&limit=${limit}${q ? `&search=${q}` : ""}`
      );
      setUsers(Array.isArray(resp.data.data) ? resp.data.data : []);
      setTotal(resp.data.total ?? 0);
      setTotalPages(resp.data.totalPages ?? 1);
    } catch (err) {
      console.error("Bulk delete user error, trying fallback:", err);
      try {
        await Promise.all(
          selectedIds.map((id) =>
            axios.delete(`${API_BASE_URL}/users/${id}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
          )
        );
        setSelectedIds([]);
        const normalized = normalizeSearch(searchQuery);
        const q = encodeURIComponent(normalized);
        const resp = await axiosClient.get(
          `/clients?page=${page}&limit=${limit}${q ? `&search=${q}` : ""}`
        );
        setUsers(Array.isArray(resp.data.data) ? resp.data.data : []);
        setTotal(resp.data.total ?? 0);
        setTotalPages(resp.data.totalPages ?? 1);
      } catch (fallbackErr) {
        setError(err.response?.data?.message || "Failed to delete selected clients.");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // when search text changes we should reset to page 1 (already present in onChange)
  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedIds((prev) => prev.filter((id) => id !== userId));

      // After deletion, refetch current page (to keep server-side pagination accurate)
      setLoading(true);
      const normalized = normalizeSearch(searchQuery);
      const q = encodeURIComponent(normalized);
      const resp = await axiosClient.get(`/clients?page=${page}&limit=${limit}${q ? `&search=${q}` : ""}`);
      setUsers(Array.isArray(resp.data.data) ? resp.data.data : []);
      setTotal(resp.data.total ?? 0);
      setTotalPages(resp.data.totalPages ?? 1);
    } catch (err) {
      setError("Failed to delete user.");
      console.error("Error deleting user:", err);
    } finally {
      setLoading(false);
    }
  };


  // advanced pagination number generator with ellipses
  const getPageNumbers = (current, totalPages) => {
    const delta = 2; // show two pages on each side of current
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= current - delta && i <= current + delta)
      ) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  // UI states
  if (loading) {
    return <div className="text-center text-gray-500 p-6">Loading...</div>;
  }

  if (error) {
    return <div className="text-center text-red-500 p-6">{error}</div>;
  }

  return (
    <div className="container mx-auto p-1 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Clients</h1>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center w-full sm:w-auto gap-2 sm:gap-4">
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1); // reset page to 1 on new search
            }}
            className="px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-64"
          />

          <Link
            to="/clients/export"
            className="flex items-center justify-center px-4 py-2 bg-blue-700 text-white rounded-lg shadow-md hover:bg-blue-700 transition"
          >
            <BiExport className="mr-2" /> Export Client
          </Link>

          <Link
            to="/clients/import"
            className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition"
          >
            <BiImport className="mr-2" /> Import Client
          </Link>

          <Link
            to="/clients/new"
            className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition"
          >
            <BiPlus className="mr-2" /> Add New Client
          </Link>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow-lg rounded-lg p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-gray-600">
            Showing <strong>{users.length}</strong> of <strong>{total}</strong> clients
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Per page:</label>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              className="px-2 py-1 border rounded"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Bulk actions banner */}
        {selectedIds.length > 0 && (
          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3 my-4 transition-all">
            <div className="flex items-center space-x-2 text-sm text-blue-800 font-medium">
              <span>{selectedIds.length} of {users.length} selected</span>
              <button
                onClick={() => setSelectedIds([])}
                className="text-xs text-blue-600 underline hover:text-blue-800 ml-2"
              >
                Clear Selection
              </button>
            </div>
            <button
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className="flex items-center px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition disabled:opacity-50"
            >
              <BiTrash className="mr-1 h-4 w-4" />
              {isDeleting ? "Deleting..." : `Delete Selected (${selectedIds.length})`}
            </button>
          </div>
        )}

        <div className="hidden sm:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={users.length > 0 && selectedIds.length === users.length}
                    onChange={handleSelectAll}
                    aria-label="Select all clients"
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Email
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {users.length > 0 ? (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      selectedIds.includes(user.id) ? "bg-blue-50/50" : ""
                    }`}
                  >
                    <td className="px-4 py-4 text-sm w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(user.id)}
                        onChange={() => handleToggleSelect(user.id)}
                        aria-label={`Select client ${user.first_name || user.agency}`}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {user.agency ? `${user.agency}${user.first_name && ' (' } ${user.first_name } ${user.last_name == 'NA' ? "":user.last_name}${user.first_name && ')' }` : `${user.first_name} ${user.last_name}`}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-500">{user.email}</td>

                    <td className="px-6 py-4 text-right text-sm font-medium space-x-3">
                      <Link
                        to={`/clients/${user.id}`}
                        className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                      >
                        <BiPencil className="h-5 w-5 mr-1" /> View / Edit
                      </Link>

                      <button
                        onClick={() => handleDelete(user.id)}
                        className="text-red-600 hover:text-red-900 inline-flex items-center"
                      >
                        <BiTrash className="h-5 w-5 mr-1" /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="sm:hidden space-y-4">
          {users.map((user) => (
            <div
              key={user.id}
              className={`border rounded-lg p-4 shadow-sm bg-gray-50 ${
                selectedIds.includes(user.id) ? "ring-2 ring-blue-500 bg-blue-50/30" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="text-lg font-semibold">
                  {user.is_agency ? user.agency : user.first_name}{" "}
                  {user.is_agency ? "" : user.last_name}
                </div>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(user.id)}
                  onChange={() => handleToggleSelect(user.id)}
                  aria-label={`Select client ${user.first_name || user.agency}`}
                  className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                />
              </div>

              <div className="text-sm text-gray-600">{user.email}</div>

              <div className="text-sm text-gray-600 mb-3">Role: {user.role_name}</div>

              <div className="flex justify-between items-center">
                <Link
                  to={`/clients/${user.id}`}
                  className="text-blue-600 hover:text-blue-900 text-sm font-medium flex items-center"
                >
                  <BiPencil className="mr-1" /> View/Edit
                </Link>

                <button
                  onClick={() => handleDelete(user.id)}
                  className="text-red-600 hover:text-red-900 text-sm font-medium flex items-center"
                >
                  <BiTrash className="mr-1" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>


{/* Pagination Controls */}
<div className="mt-6">
  <div className="
    flex flex-wrap 
    justify-center sm:justify-between 
    items-center 
    gap-3
  ">
    
    {/* Left side (First, Prev) */}
    <div className="flex flex-wrap justify-center gap-2">
      <button
        disabled={page === 1}
        onClick={() => setPage(1)}
        className={`px-3 py-1 rounded border text-sm ${
          page === 1
            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
            : "bg-white hover:bg-gray-100"
        }`}
      >
        First
      </button>

      <button
        disabled={page === 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        className={`px-3 py-1 rounded border text-sm ${
          page === 1
            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
            : "bg-white hover:bg-gray-100"
        }`}
      >
        Prev
      </button>
    </div>

    {/* Page numbers */}
    <div className="
      flex flex-wrap justify-center 
      gap-2 max-w-full
    ">
      {getPageNumbers(page, totalPages).map((pIdx, idx) =>
        pIdx === "..." ? (
          <span key={idx} className="px-2 text-gray-500 text-sm">…</span>
        ) : (
          <button
            key={pIdx}
            onClick={() => setPage(Number(pIdx))}
            className={`px-3 py-1 rounded border text-sm ${
              pIdx === page
                ? "bg-blue-600 text-white"
                : "bg-white hover:bg-gray-100"
            }`}
          >
            {pIdx}
          </button>
        )
      )}
    </div>

    {/* Right side (Next, Last) */}
    <div className="flex flex-wrap justify-center gap-2">
      <button
        disabled={page === totalPages}
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        className={`px-3 py-1 rounded border text-sm ${
          page === totalPages
            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
            : "bg-white hover:bg-gray-100"
        }`}
      >
        Next
      </button>

      <button
        disabled={page === totalPages}
        onClick={() => setPage(totalPages)}
        className={`px-3 py-1 rounded border text-sm ${
          page === totalPages
            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
            : "bg-white hover:bg-gray-100"
        }`}
      >
        Last
      </button>
    </div>
  </div>

  {/* Page indicator */}
  <div className="text-center sm:text-right text-sm text-gray-600 mt-3">
      Page <strong>{page}</strong> of <strong>{totalPages}</strong>
    </div>
  </div>

      </div>
    </div>
  );
};

export default UserList;
